import {ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit} from '@angular/core';
import {RestService as GenericRestService} from '../../../../lib/yii2-rest/rest.service';
import {ActivatedRoute, Router} from '@angular/router';
import {NotificationsService} from 'angular2-notifications';
import {FormBuilder} from '@angular/forms';
import {AuthorizationService} from '../../../authorization/authorization.service';
import {HttpClient} from '@angular/common/http';
import {RestService} from 'gelios-ng-admin-components';
import {TranslateService} from '@ngx-translate/core';
import {ImmutableListRepositoryService} from '../../../../lib/gelios-field/immutable-list-repository.service';
import {BaseFormContainer} from '../../../base/base-form-container';
import {ReflexiveRestModel} from '../../../../lib/reflexive-model/reflexive-rest-model';
import {TopUpBalanceUsingBankCardModel} from './top-up-balance-using-bank-card.model';
import {UserBalanceService} from '../../../layout/user-balance.service';
import {debounceTime, delay, filter, switchMap, take} from 'rxjs/operators';
import {TopUpBalanceUsingBankCardService} from './top-up-balance-using-bank-card.service';
import {BehaviorSubject, Subscription} from 'rxjs';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {RouteLibraryService} from '../../../route/route-library.service';
import {RouteLibraryEntities} from '../../../route/route-library-entity';
import {ProjectBusinessLogicFilterService} from '../../../shared/project-business-logic-filter/project-business-logic-filter.service';

@Component({
  selector: 'app-top-up-balance-using-bank-card-form',
  templateUrl: './top-up-balance-using-bank-card.component.html',
  providers: [
    {
      provide: GenericRestService,
      useClass: GenericRestService
    }
  ]
})
export class TopUpBalanceUsingBankCardComponent extends BaseFormContainer implements OnInit, OnDestroy {
  paymentIsPossible = true;
  bankCardPaymentFormUrl: BehaviorSubject<SafeResourceUrl | null> = new BehaviorSubject<SafeResourceUrl | null>(null);
  paymentError: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  refreshTransactionStatusUrl: string | null = null;

  subscriptions: Subscription[] = [];

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected notif: NotificationsService,
    protected formBuilder: FormBuilder,
    protected authorizationService: AuthorizationService,
    protected http: HttpClient,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
    protected changeDetector: ChangeDetectorRef,
    protected translates: TranslateService,
    protected immutableList: ImmutableListRepositoryService,
    protected userBalanceService: UserBalanceService,
    protected topUpBalanceUsingBankCardService: TopUpBalanceUsingBankCardService,
    protected sanitizer: DomSanitizer,
    protected routeLibrary: RouteLibraryService,
    protected projectBusinessLogicFilterService: ProjectBusinessLogicFilterService,
  ) {
    super(
      router,
      activatedRoute,
      notif,
      formBuilder,
      authorizationService,
      http,
      environmentVersion,
      environmentApiBasepoint,
      changeDetector,
      translates,
      immutableList,
      routeLibrary,
      projectBusinessLogicFilterService,
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.formGroupReady.pipe(filter(ready => ready), take(1)).subscribe(
      () => {
        this.subscriptions.push(
          this.formGroup.controls.amount.valueChanges.pipe(
            debounceTime(500),
            switchMap(
              value => this.topUpBalanceUsingBankCardService.getAmountInBankCardCurrency(value)
            ),
          ).subscribe(value => this.formGroup.controls.amountInBankCardCurrency.setValue(value, {emitEvent: false}))
        );
        this.subscriptions.push(
          this.formGroup.controls.amountInBankCardCurrency.valueChanges.pipe(
            debounceTime(500),
            switchMap(
              value => this.topUpBalanceUsingBankCardService.getAmountFromAmountInBankCardCurrency(value)
            )
          ).subscribe(value => this.formGroup.controls.amount.setValue(value, {emitEvent: false}))
        );
      }
    );
  }

  @HostListener('window:message', ['$event']) onMessage(event) {
    if (event.data && event.data.paymentResult) {
      if (this.refreshTransactionStatusUrl) {
        this.topUpBalanceUsingBankCardService.refreshTransactionStatus(this.refreshTransactionStatusUrl).subscribe();
      }
      switch (event.data.paymentResult) {
        case 'canceled':
          this.bankCardPaymentFormUrl.next('');
          this.paymentError.next(null);
          break;
        case 'declined':
          this.bankCardPaymentFormUrl.next('');
          this.paymentError.next(this.translates.instant('Operation declined - insufficient funds'));
          break;
        case 'success':
          this.bankCardPaymentFormUrl.next('');
          this.paymentError.next('');
          this.notif.success('Account balance successfully replenished');
          const userId = this.activatedRoute.snapshot.params.id;
          this.router.navigateByUrl(this.routeLibrary.getRoute({
            entity: RouteLibraryEntities.USER,
            action: 'view',
            id: userId,
          }));
      }
    }

  }

  fetchModel() {
    const subscription = this.userBalanceService.user.pipe(
      filter(user => !!user),
      take(1),
      delay(0)
    ).subscribe(
      user => {
        if (user.billingVer202212Status && user.user_billing_acquirer_id) {
          const model = this.createModel();
          model.fields.amount.labelAfterInput = user.billingVer202212Status.currencyCode;
          model.fields.amountInBankCardCurrency.labelAfterInput = user.userBillingAcquirerCurrencyCode;
          if (user.billingVer202212Status.balance < 0) {
            model.fields.amount.value = Math.abs(user.billingVer202212Status.balance);
            this.topUpBalanceUsingBankCardService.getAmountInBankCardCurrency(model.fields.amount.value).subscribe(
              amountInBankCardCurrency => {
                model.fields.amountInBankCardCurrency.value = amountInBankCardCurrency;
                this.model.next(model);
              }
            );
          } else {
            model.fields.amount.value = 0;
            model.fields.amountInBankCardCurrency.value = 0;
            this.model.next(model);
          }
        } else {
          this.paymentIsPossible = false;
        }
      }
    );
  }

  createModel(): ReflexiveRestModel {
    return new TopUpBalanceUsingBankCardModel();
  }
  getModelName(): string {
    return 'top-up-balance-using-bank-card';
  }
  createService(http: HttpClient, environmentVersion: string, environmentApiBasepoint: string): RestService<ReflexiveRestModel> {
    return null;
  }
  getMode(): string {
    return 'create';
  }
  successNavigate() {
    this.router.navigate(['/user/user-list']);
  }

  proceedWithPayment() {
    if (this.formGroup.valid) {
      this.topUpBalanceUsingBankCardService
        .prepareTransaction(this.formGroup.controls.amountInBankCardCurrency.value)
        .subscribe(
          value => {
            if (value.isRedirect) {
              window.location.href = value.formUrl;
            } else {
              this.bankCardPaymentFormUrl.next(this.sanitizer.bypassSecurityTrustResourceUrl(value.formUrl));
              this.refreshTransactionStatusUrl = value.refreshTransactionStatusUrl;
            }
          }
        );
    }
  }

  ngOnDestroy() {
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }
}
